import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { ErrorBoundary } from './components/ErrorBoundary';
import { AppShell } from './components/layout/AppShell';
import { OrganizationsAdminPage } from './features/admin/OrganizationsAdminPage';
import { UsersAdminPage } from './features/admin/UsersAdminPage';
import { AuditLogPage } from './features/audit/AuditLogPage';
import { AcceptInvitePage } from './features/auth/AcceptInvitePage';
import { AuthProvider } from './features/auth/auth-context';
import { LoginPage } from './features/auth/LoginPage';
import { MyDataPage } from './features/auth/MyDataPage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { Setup2FAPage } from './features/auth/Setup2FAPage';
import { AutomationDetailPage } from './features/automations/AutomationDetailPage';
import { AutomationsListPage } from './features/automations/AutomationsListPage';
import { HelpPage } from './features/help/HelpPage';
import { IncidentDetailPage } from './features/incidents/IncidentDetailPage';
import { IncidentsListPage } from './features/incidents/IncidentsListPage';
import { KpisPage } from './features/kpis/KpisPage';
import { CguPage } from './features/legal/CguPage';
import { CgvPage } from './features/legal/CgvPage';
import { ConfidentialitePage } from './features/legal/ConfidentialitePage';
import { CookiesPage } from './features/legal/CookiesPage';
import { MentionsLegalesPage } from './features/legal/MentionsLegalesPage';
import { ExecutionDetailPage } from './features/monitoring/ExecutionDetailPage';
import { ExecutionsListPage } from './features/monitoring/ExecutionsListPage';
import { RequestsPage } from './features/requests/RequestsPage';
import { SuggestionsPage } from './features/suggestions/SuggestionsPage';
import { queryClient } from './lib/query-client';
import { ProtectedRoute } from './router/protected-route';
import { RoleRoute } from './router/role-route';

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/auth/reset" element={<ResetPasswordPage />} />
              <Route path="/auth/setup-2fa" element={<Setup2FAPage />} />
              {/* Pages légales publiques (accessibles sans authentification). */}
              <Route path="/legal/mentions-legales" element={<MentionsLegalesPage />} />
              <Route path="/legal/cgv" element={<CgvPage />} />
              <Route path="/legal/cgu" element={<CguPage />} />
              <Route path="/legal/confidentialite" element={<ConfidentialitePage />} />
              <Route path="/legal/cookies" element={<CookiesPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                <Route index element={<KpisPage />} />
                <Route path="me/data" element={<MyDataPage />} />
                <Route path="aide" element={<HelpPage />} />
                <Route path="demandes" element={<RequestsPage />} />
                <Route path="suggestions" element={<SuggestionsPage />} />
                <Route path="automations" element={<AutomationsListPage />} />
                <Route path="automations/:id" element={<AutomationDetailPage />} />
                <Route path="executions" element={<ExecutionsListPage />} />
                <Route path="executions/:id" element={<ExecutionDetailPage />} />
                <Route
                  path="incidents"
                  element={
                    <RoleRoute allowed={['SUPER_ADMIN', 'ADWEB_OPERATOR']}>
                      <IncidentsListPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="incidents/:id"
                  element={
                    <RoleRoute allowed={['SUPER_ADMIN', 'ADWEB_OPERATOR']}>
                      <IncidentDetailPage />
                    </RoleRoute>
                  }
                />
                {/* Alias de l'ancienne URL /kpis vers la nouvelle Synthèse (/). */}
                <Route path="kpis" element={<Navigate to="/" replace />} />
                <Route
                  path="admin/organizations"
                  element={
                    <RoleRoute allowed={['SUPER_ADMIN', 'ADWEB_OPERATOR']}>
                      <OrganizationsAdminPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="admin/users"
                  element={
                    <RoleRoute allowed={['SUPER_ADMIN', 'ADWEB_OPERATOR', 'CLIENT_ADMIN']}>
                      <UsersAdminPage />
                    </RoleRoute>
                  }
                />
                <Route
                  path="audit"
                  element={
                    <RoleRoute allowed={['SUPER_ADMIN', 'ADWEB_OPERATOR']}>
                      <AuditLogPage />
                    </RoleRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}
